import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, CheckCircle, AlertTriangle, Clock, FileText, ChevronLeft, CreditCard, Eye, Cpu, List, Zap, X, Lock } from 'lucide-react';
import { colors, API, fmt, authHeaders, origemLabel, podeGerenciarCartoes, temOperacao, getOperacao, getTitularesVisiveis } from '../utils/theme';
import {
  PageHeader, SearchBar, StatCard, Table, Th, Td, TrHover, Badge, Button,
  Select, Input, LoadingSpinner, EmptyState, SearchableSelect,
} from '../components/UI';
import useSort from '../hooks/useSort';
import useIsMobile from '../hooks/useIsMobile';

// Valor sentinela do seletor: painel consolidado da operação inteira.
const TODOS = 'todos';

const STATUS_COLORS = {
  ENVIADO: colors.success,
  PENDENTE: colors.warning,
  PROCESSADO: colors.primary,
  ERRO: colors.danger,
};

const ALERTA_CONFIG = {
  URGENTE: { color: colors.danger, label: 'Falta < 4 dias!', icon: AlertTriangle },
  ATRASADO: { color: colors.danger, label: 'Vencido!', icon: AlertTriangle },
  PROXIMO: { color: colors.warning, label: 'Vence em breve', icon: Clock },
};

export default function PainelInvestidorPage() {
  const [titulares, setTitulares] = useState([]);
  const [selectedTitular, setSelectedTitular] = useState(null);
  const [painel, setPainel] = useState(null);
  const [mes, setMes] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [search, setSearch] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('ATIVO');
  const [incluirNegociacao, setIncluirNegociacao] = useState(false);
  // Chamado #52: "Todos os Cartões" — inclui inativos/sem_fatura pra navegar
  // meses antigos e abrir PDFs de cartões que já saíram do painel padrão.
  const [todosCartoes, setTodosCartoes] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);

  // Detectar se o usuário é restrito a UM titular (vê só os próprios dados).
  // Gestores (ADMIN/Gerente/Operador) NÃO são restritos mesmo tendo titular_id
  // vinculado — veem o painel completo de todos os titulares.
  // Quem tem OPERAÇÃO não é titular fixo: ele navega entre os investidores do
  // grupo, como um gestor navega entre todos. Sem operação, continua preso no
  // próprio titular — que é o comportamento de sempre.
  const isTitular = useCallback(() => {
    try {
      const usr = JSON.parse(localStorage.getItem('xm_usuario') || '{}');
      if (podeGerenciarCartoes()) return null;
      if (temOperacao()) return null;
      return usr.titular_id || null;
    } catch { return null; }
  }, []);

  // Se for Titular, auto-selecionar o titular vinculado
  useEffect(() => {
    const titularId = isTitular();
    if (titularId) {
      setSelectedTitular(titularId);
      setLoading(false);
    }
  }, [isTitular]);

  // Load titulares list (apenas se NÃO for Titular)
  useEffect(() => {
    if (isTitular()) return; // titular fixo (sem operação) não precisa da lista
    fetch(`${API}/faturas/titulares?mes=${mes}&status=${filtroStatus}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setTitulares(d.titulares || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [mes, isTitular, filtroStatus]);

  // Load painel for selected titular
  const loadPainel = useCallback(() => {
    if (!selectedTitular) return;
    const params = `mes=${mes}${incluirNegociacao ? '&incluir_negociacao=true' : ''}${todosCartoes ? '&todos_cartoes=true' : ''}`;

    // "Todos" = a operação inteira num painel só. O endpoint é por titular,
    // então buscamos os quatro e somamos aqui. Cada cartão carrega de quem é,
    // pra coluna TITULAR distinguir na lista consolidada.
    if (selectedTitular === TODOS) {
      const alvos = getTitularesVisiveis();
      Promise.all(alvos.map(t =>
        fetch(`${API}/faturas/painel/${t.id}?${params}`, { headers: authHeaders() })
          .then(r => r.json()).catch(() => null)))
        .then(res => {
          const validos = res.filter(d => d && !d.error);
          const cartoes = validos.flatMap(d =>
            (d.cartoes || []).map(c => ({ ...c, titular_nome: c.titular_nome || d.titular?.nome })));
          setPainel({
            titular: { id: TODOS, nome: getOperacao()?.nome ? `OPERAÇÃO ${getOperacao().nome}` : 'TODOS OS INVESTIDORES' },
            mes,
            cartoes,
            resumo: validos.reduce((a, d) => ({
              totalCartoes: a.totalCartoes + (d.resumo?.totalCartoes || 0),
              comUpload:    a.comUpload    + (d.resumo?.comUpload    || 0),
              semUpload:    a.semUpload    + (d.resumo?.semUpload    || 0),
              urgentes:     a.urgentes     + (d.resumo?.urgentes     || 0),
              atrasados:    a.atrasados    + (d.resumo?.atrasados    || 0),
            }), { totalCartoes: 0, comUpload: 0, semUpload: 0, urgentes: 0, atrasados: 0 }),
          });
        });
      return;
    }

    fetch(`${API}/faturas/painel/${selectedTitular}?${params}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        // Só aceita resposta válida — não objeto de erro
        if (d?.error) {
          console.error('Erro ao carregar painel:', d.error);
          setPainel({ titular: {}, cartoes: [], resumo: {} });
        } else {
          setPainel(d);
        }
      })
      .catch(console.error);
  }, [selectedTitular, mes, incluirNegociacao, todosCartoes]);

  useEffect(() => { loadPainel(); }, [loadPainel]);

  // Polling automático: enquanto houver fatura v2 com OCR rodando (não processado e sem erro),
  // recarrega o painel a cada 4s até tudo estabilizar. Para por si só quando termina.
  useEffect(() => {
    if (!painel?.cartoes?.length) return;
    const ocrRodando = painel.cartoes.some(c =>
      c.upload?.v2 && !c.upload.ocr_processado && !c.upload.ocr_erro
    );
    if (!ocrRodando) return;
    const t = setTimeout(() => loadPainel(), 4000);
    return () => clearTimeout(t);
  }, [painel, loadPainel]);

  const handleUploadClick = (cartao) => {
    // Guarda de duplicado (chamado #12): se já tem fatura enviada no mês, confirma
    // antes de reenviar (evita envio duplicado por engano).
    if (cartao.upload && !window.confirm(
      `O cartão "${cartao.nome_cartao}" já tem fatura enviada neste mês.\n\nEnviar de novo vai SUBSTITUIR a atual. Continuar?`)) return;
    setUploadTarget(cartao);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || !uploadTarget) return;
    const cartao = uploadTarget;
    setUploading(cartao.id);

    const formData = new FormData();
    formData.append('arquivo', file);
    formData.append('cartao_id', cartao.id);
    formData.append('titular_id', selectedTitular);
    formData.append('mes_referencia', mes);
    // valor_total e senha são opcionais — OCR descobre

    try {
      const token = localStorage.getItem('xm_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/faturas-v2`, { method: 'POST', headers, body: formData });
      const text = await res.text();
      let result;
      try { result = JSON.parse(text); } catch { throw new Error(`Servidor retornou resposta inválida (HTTP ${res.status}): ${text.slice(0, 100)}`); }
      if (!res.ok || result.error) throw new Error(result.error || `HTTP ${res.status}`);
      loadPainel();
    } catch (err) {
      alert('Erro ao enviar: ' + err.message);
    }
    setUploading(null);
    setUploadTarget(null);
  };

  const handleDelete = async (uploadId, isV2 = false, cartao = null) => {
    if (!window.confirm('Remover esta fatura? (PDF + lançamentos serão apagados)')) return;
    try {
      // Caso especial: cartão "travado" — tem pagamento em contas_a_pagar mas
      // SEM upload de PDF (uploadId vem null). Fallback: chamar reset-cartao
      // que apaga contas_a_pagar (incluindo PAGO) + pagamentos parciais.
      if (!uploadId && cartao?.id) {
        const r = await fetch(`${API}/faturas/reset-cartao/${cartao.id}`, {
          method: 'POST', headers: authHeaders(),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Erro');
        loadPainel();
        return;
      }
      const url = isV2 ? `${API}/faturas-v2/${uploadId}` : `${API}/faturas/upload/${uploadId}`;
      const r = await fetch(url, { method: 'DELETE', headers: authHeaders() });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${r.status}`);
      }
      loadPainel();
    } catch (e) { alert('Erro: ' + e.message); }
  };

  const handleResetCartao = async (cartaoId, nomeCartao) => {
    const ok = window.confirm(
      `🔥 RESETAR cartão "${nomeCartao}"?\n\n` +
      `Isso vai APAGAR TUDO de FATURA_PDF deste cartão:\n` +
      `• Todas as transações extraídas de PDFs\n` +
      `• Todas as conciliações\n` +
      `• Todos os uploads de PDFs\n` +
      `• Todas as contas a pagar pendentes\n\n` +
      `Use quando os dados estiverem duplicados/bagunçados.\n` +
      `Depois você pode fazer um upload limpo do zero.\n\n` +
      `Continuar?`
    );
    if (!ok) return;
    try {
      const r = await fetch(`${API}/faturas/reset-cartao/${cartaoId}`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erro');
      alert(
        `✓ Cartão limpo!\n\n` +
        `${d.counts.transacoes} transações removidas\n` +
        `${d.counts.faturas_upload} uploads removidos\n` +
        `${d.counts.conciliacoes} conciliações removidas\n` +
        `${d.counts.contas_a_pagar} contas a pagar removidas`
      );
      loadPainel();
    } catch (e) { alert('Erro: ' + e.message); }
  };

  if (loading) return <LoadingSpinner />;

  // Titular detail view
  if (selectedTitular && painel) {
    return <PainelDetalhe
      painel={painel} mes={mes} setMes={setMes}
      onBack={isTitular() ? null : () => { setSelectedTitular(null); setPainel(null); }}
      onUpload={handleUploadClick} uploading={uploading}
      onDelete={handleDelete} fileInputRef={fileInputRef}
      handleFileSelected={handleFileSelected}
      incluirNegociacao={incluirNegociacao}
      setIncluirNegociacao={setIncluirNegociacao}
      todosCartoes={todosCartoes}
      setTodosCartoes={setTodosCartoes}
      onResetCartao={handleResetCartao}
      setUploadTarget={setUploadTarget}
      loadPainel={loadPainel}
    />;
  }

  // Titulares list
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Painel do Investidor</h2>
          <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>Controle de upload de faturas por titular</div>
        </div>
        <input type="month" value={mes} onChange={e => setMes(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: 8, border: `1px solid ${colors.border}`,
            background: colors.bg, color: colors.text, fontSize: 14, colorScheme: 'dark',
          }}
        />
      </div>

      <SearchBar value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar titular/investidor...">
        <div style={{ display: 'flex', gap: 4, background: colors.bg, borderRadius: 8, padding: 3, border: `1px solid ${colors.border}` }}>
          {['ATIVO', 'INATIVO'].map(s => (
            <button key={s} onClick={() => setFiltroStatus(s)} style={{
              padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: filtroStatus === s ? (s === 'ATIVO' ? colors.success : colors.textDim) : 'transparent',
              color: filtroStatus === s ? '#fff' : colors.textMuted,
              transition: 'all 0.15s',
            }}>{s === 'ATIVO' ? 'Ativos' : 'Inativos'}</button>
          ))}
        </div>
        {/* Operação: um painel só, somando os investidores do grupo. Quem não
            tem operação não vê o botão — pra ele "todos" seria ele mesmo. */}
        {temOperacao() && (
          <button onClick={() => setSelectedTitular(TODOS)}
            title={`Painel consolidado da operação ${getOperacao()?.nome || ''} — todos os investidores juntos`}
            style={{
              padding: '6px 14px', borderRadius: 6, border: `1px solid ${colors.primary}`,
              background: `${colors.primary}22`, color: colors.primary,
              cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
            }}>
            ⊞ Todos ({getTitularesVisiveis().length})
          </button>
        )}
      </SearchBar>

      {titulares.filter(t => !search || t.nome.toLowerCase().includes(search.toLowerCase())).length === 0 ? (
        <EmptyState message="Nenhum titular com cartões ativos" icon={CreditCard} />
      ) : (
        <Table>
          <thead><tr>
            <Th>Titular</Th>
            <Th>Classificacao</Th>
            <Th align="center">Cartoes</Th>
            <Th align="center">Faturas Enviadas</Th>
            <Th align="center">Pendentes</Th>
            <Th>Progresso</Th>
            <Th align="center">Acoes</Th>
          </tr></thead>
          <tbody>
            {titulares.filter(t => !search || t.nome.toLowerCase().includes(search.toLowerCase())).map(t => {
              const pct = t.total_cartoes ? Math.round((t.cartoes_com_upload / t.total_cartoes) * 100) : 0;
              const pendentes = t.total_cartoes - t.cartoes_com_upload;
              return (
                <TrHover key={t.id} onClick={() => setSelectedTitular(t.id)}>
                  <Td style={{ fontWeight: 600 }}>{t.nome}</Td>
                  <Td style={{ color: colors.textMuted }}>{t.classificacao || '-'}</Td>
                  <Td style={{ textAlign: 'center' }}>{t.total_cartoes}</Td>
                  <Td style={{ textAlign: 'center', color: colors.success, fontWeight: 600 }}>{t.cartoes_com_upload}</Td>
                  <Td style={{ textAlign: 'center', color: pendentes > 0 ? colors.danger : colors.success, fontWeight: 600 }}>{pendentes}</Td>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ background: colors.bg, borderRadius: 4, overflow: 'hidden', height: 8, width: 80 }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? colors.success : pct > 50 ? colors.warning : colors.danger, borderRadius: 4, transition: 'width 0.3s' }} />
                      </div>
                      <span style={{ fontSize: 12, color: colors.textMuted, fontWeight: 600 }}>{pct}%</span>
                    </div>
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); setSelectedTitular(t.id); }}>
                      Ver Painel
                    </Button>
                  </Td>
                </TrHover>
              );
            })}
          </tbody>
        </Table>
      )}

    </div>
  );
}


// O servidor é a única autoridade sobre o diagnóstico e entrega as frases
// prontas em `mensagens`. Aqui só imprimimos — sem tabela mecanismo→prosa
// paralela e sem refrasear valor, que antes rendia dois formatos de moeda.
function resumoDiagnosticoOcr(diag) {
  if (!diag) return '';
  const partes = [];
  const faltantes = diag.coverage?.missing || [];
  if (faltantes.length) {
    partes.push(`\n\n── Linhas sem decisão do OCR ──\n${faltantes.join(', ')}`);
  }
  const mensagens = Array.isArray(diag.mensagens) ? diag.mensagens : [];
  if (mensagens.length) {
    partes.push(`\n\n── Suspeitas ──\n${mensagens.map(m => `• ${m}`).join('\n')}`);
  }
  const b = diag.balanco || {};
  const buckets = Object.keys(b)
    .map(k => `${k}: ${b[k].linhas} linhas / ${fmt(b[k].soma)}`)
    .join('\n');
  if (buckets) {
    // "soma bruta": soma dos números lidos em cada linha, não valor financeiro
    // — linha de resumo e cabeçalho entram aqui.
    partes.push(`\n\n── Soma bruta por classificação (não é valor financeiro) ──\n${buckets}`);
  }
  return partes.join('');
}

// A conta da fatura, parte por parte. O quadro-resumo do emissor declara de que
// pedaços o total é feito; esta tabela põe lado a lado o que ele DECLARA e o que
// o sistema APUROU. Sem ela, a tela mostrava o pagamento (-21.002,57) sem o
// saldo anterior que ele quita (+21.002,57) — metade de um par, e a soma dos
// lançamentos não fechava com a fatura aos olhos de quem lia.
function ComposicaoFechamento({ composicao }) {
  if (!composicao?.linhas?.length) return null;
  const divergente = composicao.status === 'divergente';
  return (
    <div style={{
      background: colors.bg, borderRadius: 8, padding: '12px 14px', marginBottom: 16,
      border: `1px solid ${divergente ? `${colors.warning}55` : colors.border}`,
    }}>
      <div style={{
        fontSize: 11, color: colors.textMuted, textTransform: 'uppercase',
        marginBottom: 8, display: 'flex', justifyContent: 'space-between',
      }}>
        <span>Composição do fechamento</span>
        <span style={{ color: divergente ? colors.warning : colors.success, fontWeight: 700 }}>
          {divergente ? '⚠ não reproduz o quadro da fatura' : '✓ confere com o quadro da fatura'}
        </span>
      </div>
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: colors.textMuted, fontSize: 11 }}>
            <th style={{ textAlign: 'left', fontWeight: 600, paddingBottom: 4 }}>Rubrica</th>
            <th style={{ textAlign: 'right', fontWeight: 600, paddingBottom: 4 }}>Na fatura</th>
            <th style={{ textAlign: 'right', fontWeight: 600, paddingBottom: 4 }}>No sistema</th>
            <th style={{ textAlign: 'right', fontWeight: 600, paddingBottom: 4 }}>Diferença</th>
          </tr>
        </thead>
        <tbody>
          {composicao.linhas.map((linha) => {
            const totalizador = linha.rubrica === 'total' || linha.rubrica === 'total_apresentado';
            return (
              <tr key={linha.rubrica} style={{
                borderTop: totalizador ? `1px solid ${colors.border}` : undefined,
                fontWeight: totalizador ? 700 : 400,
              }}>
                <Td style={{ padding: '4px 0' }}>{linha.rotulo}</Td>
                <Td align="right" style={{ padding: '4px 0', color: colors.textMuted }}>
                  {linha.declarado == null ? '—' : fmt(linha.declarado)}
                </Td>
                <Td align="right" style={{ padding: '4px 0' }}>{fmt(linha.apurado)}</Td>
                <Td align="right" style={{
                  padding: '4px 0',
                  color: linha.confere === false ? colors.warning
                    : linha.confere === true ? colors.success : colors.textDim,
                }}>
                  {linha.confere === null ? 'não publicada'
                    : linha.confere ? '✓' : fmt(linha.delta)}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PainelDetalhe({ painel, mes, setMes, onBack, onUpload, uploading, onDelete, fileInputRef, handleFileSelected, incluirNegociacao, setIncluirNegociacao, todosCartoes, setTodosCartoes, onResetCartao, setUploadTarget, loadPainel }) {
  // Mora aqui porque é aqui que é usada (selo de conferência e erro do
  // OCR). Estava em PainelInvestidorPage, outro componente: a chamada
  // quebrava com ReferenceError ao clicar.

  // Defesa: backend pode retornar { error } ou null — sempre garantir arrays/objetos
  const titular = painel?.titular || {};
  const cartoes = Array.isArray(painel?.cartoes) ? painel.cartoes : [];
  const [searchCartao, setSearchCartao] = useState('');
  const [filtroFatura, setFiltroFatura] = useState('todos'); // todos|pendentes|atrasadas|urgentes|enviadas (chamado #12)
  const [processing, setProcessing] = useState(null);
  const [transacoesModal, setTransacoesModal] = useState(null);
  const [soPessoais, setSoPessoais] = useState(false);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [planoContas, setPlanoContas] = useState([]);
  const mobile = useIsMobile();

  // ── Drop Zone IA ──────────────────────────────────────────────────────────
  const [dropZoneOpen, setDropZoneOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [loteItens, setLoteItens] = useState([]); // [{ filename, file, cartao_id, cartoes_possiveis, uploading, done, error }]
  const [loteProcessando, setLoteProcessando] = useState(false);
  const [loteEnviando, setLoteEnviando] = useState(false);
  const dropRef = useRef(null);

  const identificarLote = async (files) => {
    if (!files.length) return;
    setLoteProcessando(true);
    setLoteItens([]);
    const form = new FormData();
    form.append('titular_id', titular.id);
    const fileArr = Array.from(files);
    fileArr.forEach(f => form.append('arquivos', f));
    try {
      const r = await fetch(`${API}/faturas/identificar-lote`, { method: 'POST', body: form });
      const data = await r.json();
      if (!r.ok || data.error) {
        alert('Erro ao identificar: ' + (data.error || `HTTP ${r.status}`));
        setLoteProcessando(false);
        return;
      }
      const resultados = data.resultados || [];
      if (resultados.length === 0) {
        alert('Nenhum arquivo recebido pelo servidor. Tente novamente.');
        setLoteProcessando(false);
        return;
      }
      setLoteItens(resultados.map((res, i) => ({
        filename: res.filename,
        _fp: res._fp || {},
        file: fileArr[i],
        cartao_id: res.cartao_id || (res.cartoes_possiveis?.[0]?.id ?? ''),
        cartoes_possiveis: res.cartoes_possiveis || [],
        confianca: res.confianca || (res.cartao_id ? 'auto' : (res.cartoes_possiveis?.length > 0 ? 'sugestao' : 'manual')),
        ids_detectados: res.ids_detectados || {},
        nome_sugerido: res.nome_sugerido || null,
        nome_aceito: false,
        uploading: false,
        done: false,
        error: null,
      })));
    } catch (e) {
      alert('Erro ao identificar: ' + e.message);
    }
    setLoteProcessando(false);
  };

  const onDropFiles = (e) => {
    e.preventDefault(); setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []).filter(f => /\.pdf$/i.test(f.name));
    if (files.length) identificarLote(files);
    else if (e.dataTransfer?.files?.length) alert('Apenas arquivos PDF são aceitos.');
  };

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []).filter(f => /\.pdf$/i.test(f.name));
    if (files.length) identificarLote(files);
    e.target.value = '';
  };

  const enviarLote = async () => {
    const pendentes = loteItens.filter(i => i.cartao_id && !i.done);
    if (!pendentes.length) return;
    setLoteEnviando(true);
    for (const item of pendentes) {
      setLoteItens(prev => prev.map(x => x.filename === item.filename ? { ...x, uploading: true } : x));
      try {
        const form = new FormData();
        form.append('arquivo', item.file);
        form.append('cartao_id', item.cartao_id);
        form.append('titular_id', titular.id);
        form.append('mes_referencia', mes);
        const r = await fetch(`${API}/faturas/upload`, { method: 'POST', body: form });
        const result = await r.json();
        if (result.error) throw new Error(result.error);

        // ── Gravar fingerprint de conteúdo para aprendizado futuro ──
        fetch(`${API}/faturas/confirmar-match`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cartao_id: item.cartao_id, ...item._fp }),
        }).catch(() => {});

        setLoteItens(prev => prev.map(x => x.filename === item.filename ? { ...x, uploading: false, done: true } : x));
      } catch (e) {
        setLoteItens(prev => prev.map(x => x.filename === item.filename ? { ...x, uploading: false, error: e.message } : x));
      }
    }
    setLoteEnviando(false);
    // Recarregar painel após envio (sem reload da página inteira)
    loadPainel();
  };

  const aceitarNomeSugerido = async (item) => {
    if (!item.cartao_id || !item.nome_sugerido) return;
    try {
      const r = await fetch(`${API}/cartoes/${item.cartao_id}/renomear`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ novo_nome: item.nome_sugerido }),
      });
      const result = await r.json();
      if (result.ok) {
        setLoteItens(prev => prev.map(x => x.filename === item.filename
          ? { ...x, nome_aceito: true, manager_synced: result.manager_synced }
          : x));
      }
    } catch (e) { alert('Erro ao renomear: ' + e.message); }
  };

  // Categorias do dropdown: o plano de contas do TechMiles (18/09/2026). Era só
  // o Manager, que caiu com as credenciais fora do .env — o dropdown esvaziou em
  // silêncio. O Manager segue como fallback, depois do plano.
  useEffect(() => {
    fetch(`${API}/plano-contas/subcategorias`, { headers: authHeaders() }).then(r => r.json())
      .then(d => setPlanoContas(Array.isArray(d) ? d : []))
      .catch(() => setPlanoContas([]));
    fetch(`${API}/xmiles/auth`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(() => fetch(`${API}/xmiles/expense-types`).then(r => r.json()).then(d => {
        // Aceitar array direto, ou wrappers comuns; nunca atribuir não-array
        const arr = Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : Array.isArray(d?.expenseTypes) ? d.expenseTypes : [];
        setExpenseTypes(arr);
      }).catch(() => setExpenseTypes([])))
      .catch(() => {});
  }, []);
  // Status da fatura de um cartão, p/ filtro e ordenação (chamado #12):
  // enviada (tem upload) · atrasada (vencida sem upload) · urgente (<=4d sem
  // upload) · pendente (futura sem upload).
  const statusFatura = (c) => {
    if (c.upload) return 'enviada';
    if (c.dias_para_vencer != null && c.dias_para_vencer < 0) return 'atrasada';
    if (c.dias_para_vencer != null && c.dias_para_vencer <= 4) return 'urgente';
    return 'pendente';
  };
  // Base após negociação (p/ contar nas abas, independente do filtro de status).
  // Chamado #52: com "Todos os Cartões" ativo nada é escondido no cliente —
  // o backend já devolveu o conjunto completo (inclusive inativos/sem_fatura).
  const baseFatura = cartoes.filter(c => todosCartoes || incluirNegociacao || c.gerenciamento_crise !== 'SIM');
  const contagem = {
    todos: baseFatura.length,
    pendentes: baseFatura.filter(c => !c.upload).length,
    atrasadas: baseFatura.filter(c => statusFatura(c) === 'atrasada').length,
    urgentes: baseFatura.filter(c => statusFatura(c) === 'urgente').length,
    enviadas: baseFatura.filter(c => !!c.upload).length,
  };
  const filtered = cartoes.filter(c => {
    // Filtro 1: cartões em negociação só aparecem se um dos toggles estiver
    // ativo ("Ver em Negociação" ou "Todos os Cartões" — chamado #52).
    if (!incluirNegociacao && !todosCartoes && c.gerenciamento_crise === 'SIM') return false;
    // Filtro 2: busca textual (nome do cartão ou banco)
    if (searchCartao) {
      const q = searchCartao.toLowerCase();
      const matchNome = (c.nome_cartao || '').toLowerCase().includes(q);
      const matchBanco = (c.banco_nome || '').toLowerCase().includes(q);
      if (!matchNome && !matchBanco) return false;
    }
    // Filtro 3: status da fatura (chamado #12)
    if (filtroFatura === 'pendentes' && c.upload) return false;
    if (filtroFatura === 'enviadas' && !c.upload) return false;
    if (filtroFatura === 'atrasadas' && statusFatura(c) !== 'atrasada') return false;
    if (filtroFatura === 'urgentes' && statusFatura(c) !== 'urgente') return false;
    return true;
  });
  // Ordena por proximidade do vencimento (dias_para_vencer asc): mais vencido no
  // topo (= ordem de vencimento). O usuário pode reordenar clicando nas colunas.
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered, 'dias_para_vencer', 'asc');
  const sortState = { sortKey, sortDir };

  const handleProcess = async (cartao) => {
    if (!window.confirm(`Processar fatura de ${cartao.nome_cartao} com IA?\nIsso extrairá todos os lançamentos do PDF.`)) return;
    setProcessing(cartao.id);
    try {
      const res = await fetch(`${API}/faturas/processar/${cartao.id}/${mes}`, { method: 'POST' });
      const result = await res.json();
      if (result.error) throw new Error(result.error);
      alert(`${result.total_transacoes} lançamentos extraídos!\nValor total: R$ ${result.valor_total.toFixed(2)}\nTokens: ${result.usage.input_tokens + result.usage.output_tokens}`);
      loadPainel();
    } catch (err) {
      alert('Erro ao processar: ' + err.message);
    }
    setProcessing(null);
  };

  const handleViewTransacoes = async (cartao) => {
    setSoPessoais(false); // reseta o filtro pessoal ao abrir
    try {
      // v2: lançamentos vêm de faturas_lancamentos via /api/faturas-v2/:id
      if (cartao.upload?.v2) {
        const res = await fetch(`${API}/faturas-v2/${cartao.upload.id}`, { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        setTransacoesModal({ cartao, transacoes: Array.isArray(data?.lancamentos) ? data.lancamentos : [] });
        return;
      }
      // v1 (legado): lê de transacoes
      const res = await fetch(`${API}/transacoes?cartao_id=${cartao.id}&mes_referencia=${mes}&limit=1000`, { headers: authHeaders() });
      const data = await res.json();
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data?.transacoes) ? data.transacoes : Array.isArray(data) ? data : [];
      setTransacoesModal({ cartao, transacoes: list });
    } catch (err) {
      alert('Erro: ' + err.message);
    }
  };

  // Aceite de divergência (gestão) — a linha tem TRÊS elementos clicáveis que
  // falam da reconciliação (texto de erro, selo de conferência, Reprocessar);
  // o aceite precisa estar em todos, senão o gestor clica no lugar "errado" e
  // só vê alerta. O reprocesso com aceitar_divergencia grava as linhas lidas +
  // uma linha de AJUSTE explícita fechando no total cobrado.
  const oferecerAceiteDivergencia = async (upload, msgBase) => {
    const ehDivergencia = /CONFERÊNCIA \(visão\)/.test(upload?.ocr_erro || '');
    if (!ehDivergencia || !podeGerenciarCartoes()) { alert(msgBase); return; }
    const ok = window.confirm(msgBase +
      '\n\n──────────\nACEITAR COM DIVERGÊNCIA?\n' +
      'Grava os lançamentos lidos + uma linha de ajuste explícita com a diferença, fechando no total cobrado pela fatura.');
    if (!ok) return;
    try {
      const r = await fetch(`${API}/faturas-v2/${upload.id}/reprocessar-ocr`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ aceitar_divergencia: true }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || `HTTP ${r.status}`);
      alert('Reprocessando com divergência aceita — acompanhe o status na lista.');
      loadPainel();
    } catch (err) { alert('Erro: ' + err.message); }
  };

  const handleUpdateCategoria = async (transacao, categoria) => {
    try {
      // O texto sozinho NÃO classifica linha de fatura: joinsCategoria só
      // resolve `categoria = nome` quando origem <> 'FATURA_PDF', e é
      // justamente FATURA_PDF que esta tabela lista. Então mandamos o id do
      // plano junto — explícito, sem depender de casar por nome depois.
      // `null` quando a opção não veio do plano (Gasto Pessoal, Manager,
      // categoria local): desclassifica e o rótulo passa a valer.
      const doPlano = planoContas.find(s => s.nome === categoria);
      const r = await fetch(`${API}/transacoes/${transacao.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          categoria,
          xm_categoria_id: doPlano ? doPlano.xm_categoria_id : null,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${r.status}`);
      }
      // If "Gasto Pessoal", also mark as conciliated (exclude from conciliation)
      if (categoria === 'Gasto Pessoal' && transacao.origem === 'FATURA_PDF') {
        await fetch(`${API}/conciliacao/gasto-pessoal`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transacao_id: transacao.id }),
        });
      }
      // If was "Gasto Pessoal" and changed to something else, undo
      if (transacao.categoria === 'Gasto Pessoal' && categoria !== 'Gasto Pessoal' && transacao.origem === 'FATURA_PDF') {
        await fetch(`${API}/conciliacao/desfazer-pessoal/${transacao.id}`, { method: 'POST' });
      }
      // Espelha o que o server gravou: vindo do plano, `categoria` é o GRUPO e
      // `subcategoria` é o nome. Copiar só o texto escolhido deixaria a linha
      // mostrando uma coisa até o próximo reload mostrar outra.
      setTransacoesModal(prev => ({
        ...prev,
        transacoes: prev.transacoes.map(t => t.id === transacao.id
          ? (doPlano
              ? { ...t, categoria: doPlano.grupo, subcategoria: doPlano.nome,
                  xm_categoria_id: doPlano.xm_categoria_id }
              : { ...t, categoria, xm_categoria_id: null })
          : t),
      }));
    } catch (err) {
      alert('Erro: ' + err.message);
    }
  };

  return (
    <div>
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".pdf,.csv,.ofx,.xlsx"
        onChange={handleFileSelected} />

      {onBack && (
        <Button variant="outline" color={colors.textMuted} onClick={onBack} style={{ marginBottom: 16 }}>
          <ChevronLeft size={16} style={{ marginRight: 4 }} /> Voltar
        </Button>
      )}

      {/* Header */}
      <div style={{
        background: colors.card, borderRadius: 12, padding: mobile ? 16 : 24, marginBottom: mobile ? 14 : 20,
        border: `1px solid ${colors.border}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h3 style={{ fontSize: mobile ? 20 : 24, fontWeight: 700, margin: 0 }}>{titular.nome}</h3>
            <div style={{ color: colors.textMuted, marginTop: 4 }}>{titular.classificacao} - Painel de Faturas</div>
          </div>
          <input type="month" value={mes} onChange={e => setMes(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: 8, border: `1px solid ${colors.border}`,
              background: colors.bg, color: colors.text, fontSize: 14, colorScheme: 'dark',
            }}
          />
        </div>
      </div>

      {/* Mês anterior ao início da operação: o backend devolve vazio de propósito.
          Sem esta linha a tela ficaria em branco sem explicar por quê. */}
      {painel?.aviso && (
        <div style={{
          background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 8,
          padding: '12px 16px', marginBottom: 16, color: colors.textMuted, fontSize: 14,
        }}>
          {painel.aviso}
        </div>
      )}

      {/* Stats — contam a MESMA base da lista/abas (não-negociação por padrão),
          pra não destoar do que está visível. Antes usavam o resumo do backend
          que incluía os cartões em negociação (escondidos), causando confusão. */}
      {/* Celular: três por linha. */}
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(3, minmax(0, 1fr))' : 'repeat(5, 1fr)', gap: mobile ? 8 : 16, marginBottom: mobile ? 16 : 24 }}>
        <StatCard label="Total Cartoes" value={contagem.todos} icon={CreditCard} />
        <StatCard label="Faturas Enviadas" value={contagem.enviadas} color={colors.success} icon={CheckCircle} />
        <StatCard label="Pendentes" value={contagem.pendentes} color={contagem.pendentes > 0 ? colors.warning : colors.success} icon={FileText} />
        <StatCard label="Urgentes (< 4d)" value={contagem.urgentes} color={contagem.urgentes > 0 ? colors.danger : colors.success} icon={AlertTriangle} />
        <StatCard label="Atrasados" value={contagem.atrasadas} color={contagem.atrasadas > 0 ? colors.danger : colors.success} icon={AlertTriangle} />
      </div>

      {/* Progress bar */}
      <div style={{
        background: colors.card, borderRadius: 12, padding: 16, marginBottom: 20,
        border: `1px solid ${colors.border}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Progresso do Mes</span>
          <span style={{ fontSize: 13, color: colors.textMuted }}>
            {contagem.enviadas}/{contagem.todos} faturas enviadas
          </span>
        </div>
        <div style={{ background: colors.bg, borderRadius: 6, overflow: 'hidden', height: 12 }}>
          <div style={{
            height: '100%', borderRadius: 6, transition: 'width 0.4s',
            width: `${contagem.todos ? (contagem.enviadas / contagem.todos * 100) : 0}%`,
            background: contagem.enviadas === contagem.todos ? colors.success
              : contagem.enviadas > contagem.todos / 2 ? colors.warning : colors.danger,
          }} />
        </div>
      </div>

      {/* ── Drop Zone IA ── */}
      <input type="file" id="lote-picker" multiple accept=".pdf" style={{ display: 'none' }} onChange={onPickFiles} />

      {/* Botão para abrir */}
      <div style={{ marginBottom: 12 }}>
        <button
          onClick={() => { setDropZoneOpen(v => !v); setLoteItens([]); }}
          style={{
            padding: '10px 20px', borderRadius: 8, border: `2px dashed ${dropZoneOpen ? colors.primary : colors.border}`,
            background: dropZoneOpen ? `${colors.primary}12` : colors.bg,
            color: dropZoneOpen ? colors.primary : colors.textMuted,
            cursor: 'pointer', fontSize: 13, fontWeight: 600, width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            transition: 'all 0.2s',
          }}
        >
          <Zap size={15} />
          {dropZoneOpen ? 'Fechar zona de upload inteligente' : '⚡ Upload inteligente — arraste várias faturas de uma vez'}
        </button>
      </div>

      {dropZoneOpen && (
        <div style={{ marginBottom: 16 }}>
          {/* Drop area */}
          <div
            ref={dropRef}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDropFiles}
            onClick={() => document.getElementById('lote-picker').click()}
            style={{
              border: `2px dashed ${dragOver ? colors.primary : colors.border}`,
              borderRadius: 12, padding: '32px 24px', textAlign: 'center',
              background: dragOver ? `${colors.primary}10` : colors.bg,
              cursor: 'pointer', transition: 'all 0.2s', marginBottom: 12,
            }}
          >
            {loteProcessando ? (
              <div style={{ color: colors.primary, fontSize: 14 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🔍</div>
                Identificando cartões nos PDFs...
              </div>
            ) : (
              <>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: colors.text, marginBottom: 4 }}>
                  Arraste os PDFs das faturas aqui
                </div>
                <div style={{ fontSize: 12, color: colors.textMuted }}>
                  O sistema identifica automaticamente qual cartão é de cada fatura pelo número final
                </div>
              </>
            )}
          </div>

          {/* Tabela de mapeamento */}
          {loteItens.length > 0 && (
            <div style={{ background: colors.card, borderRadius: 10, border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: colors.text }}>
                  {loteItens.length} arquivo(s) — confirme o cartão de cada um
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setLoteItens([])}
                    style={{ background: 'none', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: colors.textMuted }}>
                    Limpar
                  </button>
                  <button onClick={enviarLote} disabled={loteEnviando || loteItens.every(i => i.done)}
                    style={{ background: colors.primary, border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#fff', opacity: loteEnviando ? 0.6 : 1 }}>
                    {loteEnviando ? 'Enviando...' : `📤 Enviar ${loteItens.filter(i => i.cartao_id && !i.done).length} fatura(s)`}
                  </button>
                </div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: colors.bg }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, width: 180 }}>Arquivo</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: colors.textMuted, fontWeight: 600 }}>Cartão / Nome</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', color: colors.textMuted, fontWeight: 600, width: 80 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loteItens.map((item, idx) => {
                    const cartaoAtual = cartoes.find(c => c.id === parseInt(item.cartao_id));
                    const nomeEditando = item.nomeEditando !== undefined ? item.nomeEditando : (cartaoAtual?.nome_cartao || '');
                    const editandoNome = item.editandoNome || false;
                    const pdfUrl = item._pdfUrl || (item.file ? URL.createObjectURL(item.file) : null);
                    return (
                    <tr key={idx} style={{ borderTop: `1px solid ${colors.border}20`, background: item.done ? `${colors.success}08` : item.error ? `${colors.danger}08` : undefined }}>
                      {/* Arquivo + botão Ver PDF */}
                      <td style={{ padding: '8px 12px', maxWidth: 180 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: colors.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, fontSize: 11 }}>
                            📄 {item.filename}
                          </span>
                          {pdfUrl && (
                            <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                              title="Abrir PDF para identificar o cartão"
                              style={{ fontSize: 11, color: colors.primary, whiteSpace: 'nowrap', textDecoration: 'none', border: `1px solid ${colors.primary}44`, borderRadius: 4, padding: '1px 5px' }}>
                              👁 ver
                            </a>
                          )}
                        </div>
                      </td>

                      {/* Seleção do cartão */}
                      <td style={{ padding: '8px 12px', minWidth: 220 }}>
                        {item.done ? (
                          <span style={{ color: colors.success, fontWeight: 600, fontSize: 12 }}>
                            ✓ {item.nome_aceito ? item.nome_sugerido : cartaoAtual?.nome_cartao || '—'}
                            {item.manager_synced && <span style={{ fontSize: 10, color: colors.primary, marginLeft: 4 }}>↑ Manager</span>}
                          </span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <select
                                value={item.cartao_id || ''}
                                onChange={e => {
                                  const cid = parseInt(e.target.value) || '';
                                  const c = cartoes.find(x => x.id === cid);
                                  setLoteItens(prev => prev.map((x, i) => i === idx
                                    ? { ...x, cartao_id: cid, nomeEditando: c?.nome_cartao || '', editandoNome: false }
                                    : x));
                                }}
                                style={{ flex: 1, padding: '4px 8px', borderRadius: 6,
                                  border: `1px solid ${item.confianca === 'auto' ? colors.success : item.confianca === 'sugestao' ? colors.warning : colors.border}`,
                                  background: colors.bg, color: colors.text, fontSize: 12 }}
                              >
                                <option value="">— selecionar cartão —</option>
                                {cartoes.map(c => (
                                  <option key={c.id} value={c.id}>{c.nome_cartao}{c.numeracao_final ? ` ···${c.numeracao_final}` : ''}</option>
                                ))}
                              </select>
                              {item.confianca === 'auto' && <span title="Auto-identificado" style={{ fontSize: 10, background: `${colors.success}22`, color: colors.success, borderRadius: 3, padding: '2px 5px', whiteSpace: 'nowrap' }}>✓ auto</span>}
                              {item.confianca === 'sugestao' && <span title="Confirme antes de enviar" style={{ fontSize: 10, background: `${colors.warning}22`, color: colors.warning, borderRadius: 3, padding: '2px 5px', whiteSpace: 'nowrap' }}>? checar</span>}
                              {item.confianca === 'manual' && <span style={{ fontSize: 10, background: `${colors.danger}22`, color: colors.danger, borderRadius: 3, padding: '2px 5px', whiteSpace: 'nowrap' }}>abrir PDF →</span>}
                            </div>
                            {/* Edição inline do nome do cartão */}
                            {item.cartao_id && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                {editandoNome ? (
                                  <>
                                    <input
                                      value={nomeEditando}
                                      onChange={e => setLoteItens(prev => prev.map((x, i) => i === idx ? { ...x, nomeEditando: e.target.value.toUpperCase() } : x))}
                                      style={{ flex: 1, padding: '3px 7px', borderRadius: 5, border: `1px solid ${colors.primary}`, background: colors.bg, color: colors.text, fontSize: 11 }}
                                      placeholder="Nome do cartão"
                                    />
                                    <button onClick={async () => {
                                      if (!nomeEditando.trim()) return;
                                      await aceitarNomeSugerido({ ...item, cartao_id: item.cartao_id, nome_sugerido: nomeEditando.trim() });
                                      setLoteItens(prev => prev.map((x, i) => i === idx ? { ...x, editandoNome: false, nome_sugerido: nomeEditando.trim() } : x));
                                    }} style={{ fontSize: 10, padding: '3px 7px', borderRadius: 4, border: 'none', background: colors.primary, color: '#fff', cursor: 'pointer' }}>✓</button>
                                    <button onClick={() => setLoteItens(prev => prev.map((x, i) => i === idx ? { ...x, editandoNome: false } : x))}
                                      style={{ fontSize: 10, padding: '3px 6px', borderRadius: 4, border: `1px solid ${colors.border}`, background: 'none', color: colors.textMuted, cursor: 'pointer' }}>✕</button>
                                  </>
                                ) : (
                                  <button onClick={() => setLoteItens(prev => prev.map((x, i) => i === idx ? { ...x, editandoNome: true, nomeEditando: item.nome_sugerido || cartaoAtual?.nome_cartao || '' } : x))}
                                    style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, border: `1px solid ${colors.border}`, background: 'none', color: colors.textDim, cursor: 'pointer' }}>
                                    ✏ {item.nome_aceito ? item.nome_sugerido : cartaoAtual?.nome_cartao || 'editar nome'}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Status */}
                      <td style={{ padding: '8px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {item.done ? <span style={{ color: colors.success, fontSize: 11 }}>✓ enviado</span>
                          : item.error ? <span style={{ color: colors.danger, fontSize: 10 }}>{item.error}</span>
                          : item.uploading ? <span style={{ color: colors.primary, fontSize: 11 }}>enviando...</span>
                          : item.cartao_id ? <span style={{ color: colors.textDim, fontSize: 11 }}>pronto</span>
                          : <span style={{ color: colors.warning, fontSize: 11 }}>👁 abra o PDF</span>}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <SearchBar value={searchCartao} onChange={e => setSearchCartao(e.target.value)} placeholder="Buscar cartão ou banco...">
        {setIncluirNegociacao && (
          <button
            onClick={() => setIncluirNegociacao(v => !v)}
            title="Inclui os cartões em negociação — também os inativos em negociação (chamado #52)"
            style={{
              padding: '6px 14px', borderRadius: 6, border: `1px solid ${incluirNegociacao ? '#f97316' : colors.border}`,
              background: incluirNegociacao ? '#f9731622' : 'transparent',
              color: incluirNegociacao ? '#f97316' : colors.textMuted,
              cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
              transition: 'all 0.15s',
            }}
          >
            {incluirNegociacao ? '✓ Em Negociação visíveis' : '+ Ver Em Negociação'}
          </button>
        )}
        {/* Chamado #52: todos os cartões do titular, qualquer status — pra
            navegar meses antigos e abrir PDFs de cartões inativos. */}
        {setTodosCartoes && (
          <button
            onClick={() => setTodosCartoes(v => !v)}
            title="Mostra todos os cartões cadastrados no titular, independente do status (inclusive inativos e sem fatura)"
            style={{
              padding: '6px 14px', borderRadius: 6, border: `1px solid ${todosCartoes ? colors.info : colors.border}`,
              background: todosCartoes ? `${colors.info}22` : 'transparent',
              color: todosCartoes ? colors.info : colors.textMuted,
              cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
              transition: 'all 0.15s',
            }}
          >
            {todosCartoes ? '✓ Todos os Cartões' : '+ Todos os Cartões'}
          </button>
        )}
      </SearchBar>

      {/* Filtro por status da fatura (chamado #12) — atalho p/ atrasados/enviadas */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, marginTop: 4 }}>
        {[
          { v: 'todos', label: 'Todos', cor: colors.text },
          { v: 'pendentes', label: 'Pendentes', cor: colors.warning },
          { v: 'atrasadas', label: 'Atrasados', cor: colors.danger },
          { v: 'urgentes', label: 'Urgentes', cor: '#f97316' },
          { v: 'enviadas', label: 'Enviadas', cor: colors.success },
        ].map(o => {
          const ativo = filtroFatura === o.v;
          return (
            <button key={o.v} onClick={() => setFiltroFatura(o.v)}
              style={{
                padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                border: `1px solid ${ativo ? o.cor : colors.border}`,
                background: ativo ? `${o.cor}22` : 'transparent',
                color: ativo ? o.cor : colors.textMuted, whiteSpace: 'nowrap', transition: 'all 0.15s',
              }}>
              {o.label} ({contagem[o.v] ?? 0})
            </button>
          );
        })}
      </div>

      {/* Cards table */}
      <Table>
        <thead><tr>
          <Th sortKey="nome_cartao" sortState={sortState} onSort={toggleSort}>Cartao</Th>
          <Th sortKey="banco_nome" sortState={sortState} onSort={toggleSort}>Banco</Th>
          <Th sortKey="numeracao_final" sortState={sortState} onSort={toggleSort}>Final</Th>
          <Th sortKey="vencimento_dia" sortState={sortState} onSort={toggleSort}>Vencimento</Th>
          <Th sortKey="dias_para_vencer" sortState={sortState} onSort={toggleSort}>Dias</Th>
          <Th sortKey="fatura_atual" sortState={sortState} onSort={toggleSort}>Fatura</Th>
          <Th sortKey="total_conciliado" sortState={sortState} onSort={toggleSort} align="right">Conciliado</Th>
          <Th sortKey="total_pessoal" sortState={sortState} onSort={toggleSort} align="right">Pessoal</Th>
          <Th>Status Upload</Th>
          <Th align="center">Acao</Th>
        </tr></thead>
        <tbody>
          {sorted.map(c => {
            const alertaCfg = c.alerta ? ALERTA_CONFIG[c.alerta] : null;
            const isUploading = uploading === c.id;

            return (
              <TrHover key={c.id} onClick={() => c.upload?.status === 'PROCESSADO' && handleViewTransacoes(c)}
                style={{
                  cursor: c.upload?.status === 'PROCESSADO' ? 'pointer' : 'default',
                  background: c.gerenciamento_crise === 'SIM' ? '#f9731608' : undefined,
                  borderLeft: c.gerenciamento_crise === 'SIM' ? '3px solid #f97316' : undefined,
                }}>
                <Td style={{ fontWeight: 600 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {c.nome_cartao}
                    {c.gerenciamento_crise === 'SIM' && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                        background: '#f9731622', color: '#f97316', border: '1px solid #f9731644',
                        whiteSpace: 'nowrap',
                      }}>
                        EM NEGOCIAÇÃO
                      </span>
                    )}
                  </div>
                  {/* No painel consolidado da operação os cartões vêm de vários
                      investidores; sem o nome do dono a lista fica ilegível. */}
                  {painel?.titular?.id === TODOS && c.titular_nome && (
                    <div style={{ fontSize: 10, color: colors.info, fontWeight: 600, marginTop: 2 }}>
                      {c.titular_nome}
                    </div>
                  )}
                </Td>
                <Td style={{ color: colors.textMuted }}>
                  {c.banco_nome || (
                    <span style={{ fontSize: 11, color: colors.warning, fontStyle: 'italic' }}>Sem banco</span>
                  )}
                </Td>
                <Td>{c.numeracao_final || '-'}</Td>
                <Td style={{ whiteSpace: 'nowrap' }}>
                  <span
                    title="Clique para editar o dia de vencimento do cartão"
                    style={{ cursor: 'pointer', textDecoration: c.vencimento_dia ? 'none' : 'underline dotted', color: c.vencimento_dia ? colors.text : colors.warning }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      const atual = (c.data_vencimento && /^\d+$/.test(String(c.data_vencimento))) ? c.data_vencimento : '';
                      const dia = window.prompt(`Dia de vencimento do cartão "${c.nome_cartao}" (1 a 31):`, atual);
                      if (dia === null) return;
                      const n = parseInt(dia);
                      if (!Number.isInteger(n) || n < 1 || n > 31) { alert('Dia inválido'); return; }
                      try {
                        const token = localStorage.getItem('xm_token');
                        const r = await fetch(`${API}/cartoes/${c.id}/dia-vencimento`, {
                          method: 'PATCH',
                          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
                          body: JSON.stringify({ dia: n }),
                        });
                        const d = await r.json();
                        if (!r.ok) throw new Error(d.error || 'Erro');
                        loadPainel();
                      } catch (err) { alert('Erro: ' + err.message); }
                    }}
                  >
                    {c.vencimento_dia || '✏ definir'}
                  </span>
                </Td>
                <Td>
                  {c.upload ? (
                    // Fatura já tem upload (enviada/processada/paga): não mostrar
                    // "Xd atrasado" — mostrar "Pago" se quitada ou "Atualizada".
                    c.upload.pago ? (
                      <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 700 }}>OK</span>
                    ) : c.upload.parcial ? (
                      <span style={{ fontSize: 12, color: '#3b82f6', fontWeight: 700 }}>Parcial</span>
                    ) : (
                      <span style={{ fontSize: 12, color: colors.primary, fontWeight: 600 }}>Atualizada</span>
                    )
                  ) : c.dias_para_vencer !== null ? (
                    <span style={{
                      color: c.dias_para_vencer < 0 ? colors.danger : c.dias_para_vencer <= 4 ? colors.warning : colors.textMuted,
                      fontWeight: c.dias_para_vencer <= 4 ? 700 : 400,
                    }}>
                      {c.dias_para_vencer < 0 ? `${Math.abs(c.dias_para_vencer)}d atrasado` : `${c.dias_para_vencer}d`}
                    </span>
                  ) : '-'}
                </Td>
                <Td style={{ fontWeight: 600, color: colors.danger }}>
                  {(() => {
                    // Prioridade: upload v2 (valor_total da fatura digitada/OCR) > fatura_atual do cartão
                    const valor = c.upload?.v2 ? c.upload.valor_total_fatura : c.fatura_atual;
                    if (!valor) return '-';
                    return (
                      <span title={c.upload?.v2 ? 'Valor da fatura uploaded' : 'fatura_atual do cadastro do cartão'}>
                        {fmt(valor)}
                      </span>
                    );
                  })()}
                </Td>
                {/* Conciliado / Pessoal (chamado #5/#7) */}
                <Td style={{ textAlign: 'right', color: colors.success, fontSize: 13 }}>
                  {(c.total_conciliado || 0) > 0 ? fmt(c.total_conciliado) : <span style={{ color: colors.textDim }}>—</span>}
                </Td>
                <Td style={{ textAlign: 'right', color: colors.warning, fontSize: 13 }}>
                  {(c.total_pessoal || 0) > 0 ? fmt(c.total_pessoal) : <span style={{ color: colors.textDim }}>—</span>}
                </Td>
                <Td>
                  {c.upload ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {c.upload.pago ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <CheckCircle size={14} style={{ color: '#22c55e' }} />
                          <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 700 }}>
                            Pago{c.upload.data_pagamento ? ` em ${c.upload.data_pagamento.slice(8,10)}/${c.upload.data_pagamento.slice(5,7)}` : ''}
                          </span>
                          {c.upload.valor_pago ? (
                            <span style={{ fontSize: 11, color: colors.textMuted }}>{fmt(c.upload.valor_pago)}</span>
                          ) : null}
                        </div>
                      ) : c.upload.parcial ? (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <Clock size={14} style={{ color: '#3b82f6' }} />
                            <span style={{ fontSize: 12, color: '#3b82f6', fontWeight: 700 }}>
                              Parcial {c.upload.valor_total_fatura > 0 ? `${Math.round((c.upload.total_pago_acumulado / c.upload.valor_total_fatura) * 100)}%` : ''}
                            </span>
                          </div>
                          <span style={{ fontSize: 11, color: colors.textMuted }}>
                            pago: {fmt(c.upload.total_pago_acumulado || 0)}
                          </span>
                          <span style={{ fontSize: 11, color: colors.warning }}>
                            saldo: {fmt(c.upload.saldo_restante || 0)}
                          </span>
                        </>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <CheckCircle size={14} style={{ color: c.upload.status === 'PROCESSADO' ? colors.primary : colors.success }} />
                          <span style={{ fontSize: 12, color: c.upload.status === 'PROCESSADO' ? colors.primary : colors.success, fontWeight: 600 }}>
                            {c.upload.status === 'PROCESSADO' ? `Processado (${c.upload.total_transacoes} lanç.)` : 'Enviado'}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : alertaCfg ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <alertaCfg.icon size={14} style={{ color: alertaCfg.color }} />
                      <span style={{ fontSize: 12, color: alertaCfg.color, fontWeight: 600 }}>{alertaCfg.label}</span>
                    </div>
                  ) : (
                    <span style={{ fontSize: 12, color: colors.textDim }}>Pendente</span>
                  )}
                </Td>
                <Td style={{ textAlign: 'center' }}>
                  {c.upload ? (
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <a href={(() => {
                          const tk = localStorage.getItem('xm_token') || '';
                          return c.upload.v2 ? `${API}/faturas-v2/${c.upload.id}/pdf?token=${encodeURIComponent(tk)}` : `${API}/faturas/pdf/${c.id}/${mes}?token=${encodeURIComponent(tk)}`;
                        })()}
                        target="_blank" rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ textDecoration: 'none' }}>
                        <Button size="sm" variant="outline" color={colors.success} icon={Eye}>PDF</Button>
                      </a>
                      {(c.upload.status === 'PROCESSADO' || c.upload.pago || (c.upload.total_transacoes || 0) > 0) && (
                        <Button size="sm" variant="outline" color={colors.primary} icon={List}
                          onClick={(e) => { e.stopPropagation(); handleViewTransacoes(c); }}>
                          Lanç.
                        </Button>
                      )}
                      {c.upload.v2 ? (
                        <>
                          {c.upload.ocr_erro && (
                            <span
                              title="Clique para ver erro completo"
                              style={{ fontSize: 11, color: colors.danger, fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                oferecerAceiteDivergencia(c.upload,
                                  'Erro do OCR:\n\n' + c.upload.ocr_erro + resumoDiagnosticoOcr(c.upload.ocr_diagnostico));
                              }}
                            >
                              ⚠ OCR falhou: {c.upload.ocr_erro.slice(0, 40)} (clique)
                            </span>
                          )}
                          {!c.upload.ocr_processado && !c.upload.ocr_erro && (
                            <span style={{ fontSize: 11, color: colors.textMuted, fontStyle: 'italic' }}>OCR rodando...</span>
                          )}
                          {/* Chamado #58: conferência do OCR não fechou — a soma dos
                              lançamentos difere do total da fatura. Avisa NA HORA. */}
                          {Math.abs(c.upload.ocr_conferencia_delta || 0) > 1 && (
                            <span
                              title="Clique para ver os detalhes da conferência"
                              style={{
                                fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                                color: colors.warning, background: `${colors.warning}18`,
                                border: `1px solid ${colors.warning}55`, borderRadius: 4, padding: '1px 6px',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const d = c.upload.ocr_conferencia_delta;
                                const det = Array.isArray(c.upload.ocr_erros_validacao)
                                  ? c.upload.ocr_erros_validacao.join('\n\n') : '';
                                oferecerAceiteDivergencia(c.upload,
                                  `Conferência do OCR não fechou\n\n` +
                                  `Diferença: ${fmt(d)}\n` +
                                  resumoDiagnosticoOcr(c.upload.ocr_diagnostico) +
                                  (det ? `\n\n── Detalhes ──\n${det}` : ''));
                              }}
                            >
                              ⚠ Conferência: {fmt(c.upload.ocr_conferencia_delta)}
                            </span>
                          )}
                          {/* Gravou SEM conferência: o total não foi extraído do PDF, então
                              a soma não foi confrontada com nada. Sem este badge o estado
                              era invisível — a fatura ficava igual a uma que conferiu. */}
                          {c.upload.ocr_processado && c.upload.ocr_conferencia_delta == null && (
                            <span
                              title="Clique para entender por que não houve conferência"
                              style={{
                                fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                                color: colors.warning, background: `${colors.warning}18`,
                                border: `1px dashed ${colors.warning}77`, borderRadius: 4, padding: '1px 6px',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const det = Array.isArray(c.upload.ocr_erros_validacao)
                                  ? c.upload.ocr_erros_validacao.join('\n\n') : '';
                                alert(
                                  'Lançamentos gravados SEM conferência\n\n' +
                                  'O total da fatura não foi extraído do PDF, então a soma dos ' +
                                  'lançamentos não foi confrontada com nada. O valor pode estar ' +
                                  'certo — só não há prova de que está.\n\n' +
                                  'Confira o total no PDF e use "Reprocessar OCR" se necessário.' +
                                  (det ? `\n\n── Alertas ──\n${det}` : '') +
                                  resumoDiagnosticoOcr(c.upload.ocr_diagnostico)
                                );
                              }}
                            >
                              ⚠ sem conferência
                            </span>
                          )}
                          <Button size="sm" variant="outline" color={colors.warning} icon={Cpu}
                            title="Reprocessa o PDF: apaga lançamentos atuais e refaz extração + recalcula valor_total"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm('Reprocessar OCR? Apaga lançamentos atuais e refaz a extração do PDF.')) return;
                              try {
                                const token = localStorage.getItem('xm_token');
                                const r = await fetch(`${API}/faturas-v2/${c.upload.id}/reprocessar-ocr`, {
                                  method: 'POST',
                                  headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
                                  body: '{}',
                                });
                                const d = await r.json();
                                if (!r.ok) throw new Error(d.error || 'Erro');
                                // Painel já tem polling automático — só recarrega o estado
                                loadPainel();
                              } catch (err) { alert('Erro: ' + err.message); }
                            }}>
                            Reprocessar
                          </Button>
                          <Button size="sm" variant="outline" color={colors.info} icon={Lock}
                            title="Reprocessa informando a senha do PDF (BB, Porto, etc. precisam)"
                            onClick={async (e) => {
                              e.stopPropagation();
                              const senha = window.prompt('Senha do PDF (4 dígitos, CPF, CEP… o que o banco pede):');
                              if (!senha) return;
                              try {
                                const token = localStorage.getItem('xm_token');
                                const r = await fetch(`${API}/faturas-v2/${c.upload.id}/reprocessar-ocr`, {
                                  method: 'POST',
                                  headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ senha_pdf: senha }),
                                });
                                const d = await r.json();
                                if (!r.ok) throw new Error(d.error || 'Erro');
                                loadPainel();
                              } catch (err) { alert('Erro: ' + err.message); }
                            }}>
                            Senha
                          </Button>
                        </>
                      ) : c.upload.status !== 'PROCESSADO' && (
                        <Button size="sm" color={colors.warning} icon={Cpu}
                          onClick={(e) => { e.stopPropagation(); handleProcess(c); }} disabled={processing === c.id}>
                          {processing === c.id ? 'Processando...' : 'Processar'}
                        </Button>
                      )}
                      {/* Remover/Resetar apagam lançamento, conciliação e conta
                          a pagar — é desfazer contabilidade, trabalho da gestão.
                          O investidor sobe a fatura dele e acompanha; se algo
                          saiu errado, abre chamado. O servidor também barra
                          (faturas.js / faturas-v2.js), isto aqui é só não
                          oferecer o botão. (05/10/2026) */}
                      {podeGerenciarCartoes() && (
                        <>
                          <Button size="sm" variant="outline" color={colors.danger}
                            onClick={(e) => { e.stopPropagation(); onDelete(c.upload.id, c.upload.v2, c); }}>
                            Remover
                          </Button>
                          <Button size="sm" variant="outline" color={colors.danger}
                            title="Apaga TODAS as transações/uploads/contas a pagar deste cartão. Use quando estiver bagunçado."
                            onClick={(e) => { e.stopPropagation(); onResetCartao(c.id, c.nome_cartao); }}>
                            🔥 Resetar
                          </Button>
                        </>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Button size="sm" color={alertaCfg ? alertaCfg.color : colors.primary}
                        onClick={(e) => { e.stopPropagation(); onUpload(c); }} disabled={isUploading}
                        icon={Upload}>
                        {isUploading ? 'Enviando...' : 'Upload'}
                      </Button>
                      <Button size="sm" variant="outline" color={c.senha_pdf ? colors.success : colors.info} icon={Lock}
                        title={c.senha_pdf ? `Senha cadastrada: ${c.senha_pdf.replace(/./g, '•')}. Clique para alterar.` : 'Cadastrar senha do PDF (uma vez só)'}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const atual = c.senha_pdf || '';
                          const senha = window.prompt(`Senha do PDF do cartão "${c.nome_cartao}" (deixe vazio para remover):`, atual);
                          if (senha === null) return;
                          try {
                            const token = localStorage.getItem('xm_token');
                            const r = await fetch(`${API}/cartoes/${c.id}/senha-pdf`, {
                              method: 'PATCH',
                              headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
                              body: JSON.stringify({ senha_pdf: senha }),
                            });
                            const d = await r.json();
                            if (!r.ok) throw new Error(d.error || 'Erro');
                            loadPainel();
                          } catch (err) { alert('Erro: ' + err.message); }
                        }}>
                        {c.senha_pdf ? 'Senha ✓' : 'Senha'}
                      </Button>
                      {podeGerenciarCartoes() && (
                        <Button size="sm" variant="outline" color={colors.danger}
                          title="Apaga TODAS as transações/uploads/contas a pagar deste cartão"
                          onClick={(e) => { e.stopPropagation(); onResetCartao(c.id, c.nome_cartao); }}>
                          🔥 Reset
                        </Button>
                      )}
                    </div>
                  )}
                </Td>
              </TrHover>
            );
          })}
        </tbody>
      </Table>

      {/* Modal de Lançamentos do Cartão */}
      {transacoesModal && (() => {
        const transTodas = Array.isArray(transacoesModal.transacoes) ? transacoesModal.transacoes : [];
        const ehPessoal = (t) => t.categoria === 'Gasto Pessoal';
        const trans = soPessoais ? transTodas.filter(ehPessoal) : transTodas;
        const conciliados = trans.filter(t => t.conciliado).length;
        // Valores v2 vêm como string (NUMERIC do Postgres) — parseFloat pra evitar NaN
        const totalValor = trans.reduce((s, t) => s + (parseFloat(t.valor) || 0), 0);
        // Total pessoal = lançamentos marcados como "Gasto Pessoal" (o que o investidor paga)
        const totalPessoal = transTodas.filter(ehPessoal).reduce((s, t) => s + (parseFloat(t.valor) || 0), 0);
        const qtdPessoal = transTodas.filter(ehPessoal).length;
        const categorias = [...new Set(transTodas.map(t => t.categoria).filter(Boolean))].sort();

        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
          }} onClick={() => setTransacoesModal(null)}>
            <div style={{
              background: colors.card, borderRadius: 16, padding: mobile ? 14 : 24, maxWidth: 1100, width: mobile ? 'calc(100% - 16px)' : '95%',
              maxHeight: mobile ? '96vh' : '90vh', overflow: 'auto', border: `1px solid ${colors.border}`,
            }} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
                    {transacoesModal.cartao.nome_cartao}
                  </h3>
                  <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
                    {transacoesModal.cartao.banco_nome} • Ref: {mes}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <a href={(() => {
                      const tk = localStorage.getItem('xm_token') || '';
                      const up = transacoesModal.cartao.upload;
                      return up?.v2
                        ? `${API}/faturas-v2/${up.id}/pdf?token=${encodeURIComponent(tk)}`
                        : `${API}/faturas/pdf/${transacoesModal.cartao.id}/${mes}?token=${encodeURIComponent(tk)}`;
                    })()}
                    target="_blank" rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()} style={{ textDecoration: 'none' }}>
                    <Button size="sm" variant="outline" color={colors.success} icon={Eye}>
                      Ver PDF
                    </Button>
                  </a>
                  <Button variant="outline" color={colors.textMuted} onClick={() => setTransacoesModal(null)}>Fechar</Button>
                </div>
              </div>

              {/* Resumo */}
              <div style={{
                display: 'grid', gridTemplateColumns: mobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, 1fr)', gap: mobile ? 8 : 12, marginBottom: 16,
              }}>
                <div style={{ background: colors.bg, borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase' }}>Lançamentos</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{trans.length}</div>
                </div>
                <div style={{ background: colors.bg, borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase' }}>Valor Total</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: colors.danger }}>{fmt(totalValor)}</div>
                </div>
                {/* Total pessoal a pagar pelo investidor (chamado #5) — clicável pra filtrar */}
                <button
                  onClick={() => setSoPessoais(v => !v)}
                  title="Lançamentos marcados como Gasto Pessoal — clique para filtrar só estes"
                  style={{
                    background: soPessoais ? `${colors.warning}22` : colors.bg, borderRadius: 8, padding: '10px 14px',
                    textAlign: 'center', cursor: 'pointer',
                    border: `1px solid ${soPessoais ? colors.warning : 'transparent'}`,
                  }}>
                  <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase' }}>
                    Total Pessoal ({qtdPessoal})
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: colors.warning }}>{fmt(totalPessoal)}</div>
                  <div style={{ fontSize: 10, color: colors.textDim, marginTop: 2 }}>
                    {soPessoais ? '✓ filtrando' : 'clique p/ filtrar'}
                  </div>
                </button>
                <div style={{ background: colors.bg, borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase' }}>Origem</div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>
                    {[...new Set(trans.map(t => t.origem))].map(o => (
                      <Badge key={o} text={origemLabel(o)} color={o === 'FATURA_PDF' ? colors.primary : o === 'XMILES' ? colors.info : o === 'XM_PRODUCAO' ? colors.success : colors.textMuted} />
                    ))}
                  </div>
                </div>
              </div>

              <ComposicaoFechamento composicao={transacoesModal.cartao.upload?.ocr_composicao} />

              {/* Tabela */}
              <Table>
                <thead><tr>
                  <Th>Data</Th>
                  <Th>Descrição</Th>
                  <Th>Parcela</Th>
                  <Th>Categoria</Th>
                  <Th>Origem</Th>
                  <Th align="right">Valor</Th>
                </tr></thead>
                <tbody>
                  {trans.map((t, i) => (
                    <tr key={t.id || i} style={{
                      borderBottom: `1px solid ${colors.border}`,
                      background: t.categoria === 'Gasto Pessoal' ? `${colors.warning}12` : undefined,
                    }}>
                      <Td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{t.data_compra || '-'}</Td>
                      <Td style={{ fontSize: 13, maxWidth: 280 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.descricao}
                        </div>
                        {/* Chamado #59: nome do fidelizado (titular da conta de
                            fidelidade) nos lançamentos vindos do TechMiles — sem
                            ele os clubes ficam todos com a mesma descrição
                            genérica e não dá pra saber de quem é. Mesmo padrão
                            visual da tela de Conciliação (#60). */}
                        {t.fidelizado && (
                          <div style={{ fontSize: 10, marginTop: 2, color: colors.info, fontWeight: 600 }}
                            title="Fidelizado — titular da conta de fidelidade deste lançamento">
                            👤 {t.fidelizado}
                          </div>
                        )}
                      </Td>
                      <Td style={{ fontSize: 12, color: colors.textMuted, textAlign: 'center' }}>{t.parcela || '-'}</Td>
                      <Td>
                        <SearchableSelect
                          small
                          // Vinculada ao plano? `categoria` guarda o GRUPO; o que
                          // se escolhe aqui é a subcategoria — é ela que casa
                          // com as opções e precisa aparecer selecionada.
                          value={(t.xm_categoria_id ? t.subcategoria : t.categoria) || ''}
                          onChange={val => handleUpdateCategoria(t, val)}
                          placeholder="Sem categoria"
                          style={{ maxWidth: 160 }}
                          options={[
                            { value: 'Gasto Pessoal', label: 'Gasto Pessoal', group: 'Controle' },
                            // Plano de contas do TechMiles: agrupado pelo grupo do plano.
                            ...planoContas.map(s => ({ value: s.nome, label: s.nome, group: s.grupo })),
                            ...expenseTypes.filter(e => e.typeTransaction === 'EXPENSE' && !planoContas.some(s => s.nome === e.expenseName)).map(e => ({ value: e.expenseName, label: e.expenseName, group: 'Despesa' })),
                            ...expenseTypes.filter(e => e.typeTransaction === 'RECEIPT' && !planoContas.some(s => s.nome === e.expenseName)).map(e => ({ value: e.expenseName, label: e.expenseName, group: 'Receita' })),
                            // As já usadas nos lançamentos, que não estão em nenhuma das fontes.
                            ...categorias.filter(c => c !== 'Gasto Pessoal'
                              && !expenseTypes.some(e => e.expenseName === c)
                              && !planoContas.some(s => s.nome === c)).map(c => ({ value: c, label: c })),
                          ]}
                        />
                      </Td>
                      <Td>
                        <Badge text={origemLabel(t.origem)} color={t.origem === 'FATURA_PDF' ? colors.primary : t.origem === 'XMILES' ? colors.info : t.origem === 'XM_PRODUCAO' ? colors.success : colors.textMuted} />
                      </Td>
                      <Td style={{
                        textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap',
                        color: t.valor < 0 ? colors.success : colors.text,
                      }}>
                        {fmt(t.valor)}
                      </Td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: `2px solid ${colors.border}` }}>
                    <Td colSpan={6} style={{ fontWeight: 700, textAlign: 'right' }}>TOTAL</Td>
                    <Td style={{ fontWeight: 700, textAlign: 'right', color: colors.danger }}>
                      {fmt(totalValor)}
                    </Td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
